import { useEffect, useMemo, useRef, useState } from "react";

import { useMemberToobarContext } from "CrossFeatureComponents/MemberToolbar/MemberToolbarState";
import { contentKeys } from "CrossFeatureComponents/MemberToolbar/contentKeys";
import { useHasActiveContent } from "CrossFeatureComponents/MemberToolbar/stateHooks";
import { LOGIN_CONTENT_PADDING } from "CrossFeatureComponents/MemberToolbar/styleConstants";

import FnrFade from "UI/FnrFade";
import FnrSlides from "UI/FnrSlides";

import { Divider } from "../shared";

import ContactForm from "./ContactForm";
import ContactMenu from "./ContactMenu";
import Login from "./Login";
import MessageSent from "./MessageSent";
import { UserMenu } from "./UserMenu/UserMenu";

const activeContentSwitchConfig = [
  { key: contentKeys.nothing, value: () => null },
  { key: contentKeys.user, value: UserMenu },
  { key: contentKeys.login, value: Login },
  { key: contentKeys.contact, value: ContactMenu },
  { key: contentKeys.form, value: ContactForm, keepInDOM: true },
  { key: contentKeys.messageSent, value: MessageSent },
];

const ActiveContent = () => {
  const { activeContentKey: activeContentKeyState } = useMemberToobarContext();
  const [activeContentKeyLocal, setActiveContentKeyLocal] = useState(
    activeContentKeyState
  );
  const [variant, setVariant] = useState("fade");
  const timeoutRef = useRef();

  // We don't want to update the slides until it has had a chance to know what variant to use.
  useEffect(() => {
    setActiveContentKeyLocal(activeContentKeyState);
  }, [activeContentKeyState]);

  useEffect(() => {
    // if going from nothing to something
    if (
      activeContentKeyLocal === "nothing" &&
      activeContentKeyState !== "nothing"
    ) {
      setVariant("fade");
      return;
    }

    // if going from something to nothing
    if (
      activeContentKeyLocal !== "nothing" &&
      activeContentKeyState === "nothing"
    ) {
      setVariant("fade");
      return;
    }

    // if going from something to something
    // This is where we land when nothing is changing. So we delay the standard from being set since it will be ready to go by default.
    if (
      activeContentKeyLocal !== "nothing" &&
      activeContentKeyState !== "nothing"
    ) {
      timeoutRef.current = setTimeout(() => setVariant("standard"), 100);
      return;
    }
  }, [activeContentKeyState, activeContentKeyLocal]);

  const isUserContent = useMemo(
    () =>
      activeContentKeyState !== "nothing" && activeContentKeyState !== "login",
    [activeContentKeyState]
  );

  const hasActiveContent = useHasActiveContent();

  return (
    <>
      <FnrSlides
        sizeSpring={hasActiveContent} // only spring when going from nothing to something
        activeKey={activeContentKeyLocal}
        variant={variant}
        fullWidth
        components={activeContentSwitchConfig}
      />
      <FnrFade show={isUserContent}>
        <Divider marginX={12} />
      </FnrFade>
    </>
  );
};

export { ActiveContent, LOGIN_CONTENT_PADDING };
